import { useCacheService } from '@fs/zion-cache-service'
import { cacheOld, cacheServiceOld } from './caches'
/**
 * Gets a list of summaries (name, id, displayName) for each of the user's groups.
 * @throws 400 - The Group id wasn't valid.
 * @throws 403 - Only un-banished group members can view a group.
 * @throws 404 - Group not found.
 * @throws 410 - Group is tombstoned.
 * @param {boolean} resetCache - whether to reset cache or not
 * @returns {Promise} promise of cacheService.get
 */
export default function getGroupsList(resetCache) {
  if (resetCache) cacheOld.removeItem('group-list')
  return cacheServiceOld
    .get(`/service/tree/tree-data/groups/current-groups`, 'group-list')
    .then((res) => res.memberSummaries)
}

export const useGroupsList = () => {
  return useCacheService(cacheServiceOld, `/service/tree/tree-data/groups/current-groups`, 'group-list')
}

function filterGroupsByType(groups, isFamily) {
  return groups.filter(({ types }) =>
    isFamily ? !types.includes('RESEARCHER_TREE') : types.includes('RESEARCHER_TREE')
  )
}

function isFailedFile(fileStatus, treeId) {
  return (
    fileStatus === 'FAILED_UPLOAD' || fileStatus === 'FAILED_PROCESSING' || (fileStatus === 'FAILED_IMPORT' && !treeId)
  )
}

function shouldIncludeFailedFile(file, groups) {
  const { fileStatus, treeId, groupId } = file

  // For researcher groups, show failures and processing states
  return (
    isFailedFile(fileStatus, treeId) ||
    (isInProcessingState(fileStatus) && !treeId) ||
    (isInProcessingState(fileStatus) && treeId && !groups?.some(({ id }) => id === groupId))
  )
}

function convertFilesToGroups(gedcomFileList, isFamily, groups) {
  if (!gedcomFileList || isFamily) return []

  return gedcomFileList
    .filter((file) => shouldIncludeFailedFile(file, groups))
    .map((file) => ({
      file,
      name: file.title,
      fileStatus: file.fileStatus,
      isFailed: true,
      isProcessing: isInProcessingState(file.fileStatus),
      types: [],
    }))
}

function enhanceTreesWithFileState(group, gedcomFileList, isFamily) {
  // Family groups don't need file state updates
  if (isFamily) return group

  const fileAssociatedWithTree = gedcomFileList?.find(({ groupId }) => groupId === group.id)

  return {
    ...group,
    isProcessing: group.isProcessing || fileAssociatedWithTree?.fileStatus === 'IMPORTING',
    needToRefetchGroups: !fileAssociatedWithTree,
  }
}

export const sortAndFilterGroups = ({ groups, isFamily, gedcomFileList }) => {
  const filteredGroups = filterGroupsByType(groups, isFamily)
  const fileGroups = convertFilesToGroups(gedcomFileList, isFamily, groups)
  const allGroups = [...filteredGroups, ...fileGroups]
  const enhancedGroups = allGroups.map((group) => enhanceTreesWithFileState(group, gedcomFileList, isFamily))
  return enhancedGroups.sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
}

function isInProcessingState(fileStatus) {
  // Of these file statuses IMPORTING is the only one that will have a treeId
  return ['IMPORTING', 'UPLOADING', 'PROCESSING', 'PROCESSED'].includes(fileStatus)
}
